/**
 * ProvenanceDrawer — Pure render component for the chain-of-custody detail view.
 *
 * Receives a fully-fetched ActionProvenanceChain and renders:
 *   Zone 1: Verdict panel (status, summary, actions)
 *   Zone 2: Seven custody bands (human-readable default, audit raw on expand)
 *   Zone 3: Receipt chain visualization
 *   Zone 4: Server-verified integrity checks
 *
 * This component does NOT fetch data. Use ProvenanceDrawerContainer for data fetching.
 *
 * Blue Steel compliance:
 *   - Zero hardcoded colors
 *   - WCAG 2.1 AA keyboard navigation
 *   - prefers-reduced-motion respected
 *   - Full data-testid instrumentation
 */

import type {
  ActionProvenanceChain,
  ProvenanceBand,
  ProvenanceReceipt,
  ChainVerification,
  ProvenanceBandState,
  ProvenanceBandSummary,
  Severity,
} from './provenance-types';

const React = (globalThis as any).React;
const { useState, useEffect, useCallback, useRef } = React;

// ============================================================================
// Sub-component imports (inline to avoid circular deps in plugin bundle)
// ============================================================================

import { ProvenanceShield } from './ProvenanceShield';

// ============================================================================
// Props
// ============================================================================

export interface ProvenanceDrawerProps {
  chain: ActionProvenanceChain;
  exportPermission?: 'redacted' | 'full_audit';
}

// ============================================================================
// Style constants
// ============================================================================

const SEVERITY_COLORS: Record<Severity, { bg: string; border: string; text: string }> = {
  success: {
    bg: 'var(--provenance-verdict-success-bg, color-mix(in srgb, var(--color-success, #22c55e) 8%, transparent))',
    border: 'var(--color-success, #22c55e)',
    text: 'var(--color-success, #22c55e)',
  },
  info: {
    bg: 'var(--provenance-verdict-info-bg, color-mix(in srgb, var(--color-info, #3b82f6) 8%, transparent))',
    border: 'var(--color-info, #3b82f6)',
    text: 'var(--color-info, #3b82f6)',
  },
  warning: {
    bg: 'var(--provenance-verdict-warning-bg, color-mix(in srgb, var(--color-warning, #f59e0b) 8%, transparent))',
    border: 'var(--color-warning, #f59e0b)',
    text: 'var(--color-warning, #f59e0b)',
  },
  critical: {
    bg: 'var(--provenance-verdict-critical-bg, color-mix(in srgb, var(--color-danger, #ef4444) 8%, transparent))',
    border: 'var(--color-danger, #ef4444)',
    text: 'var(--color-danger, #ef4444)',
  },
};

const STATE_INDICATORS: Record<ProvenanceBandState, { icon: string; color: string }> = {
  verified: { icon: '\u2713', color: 'var(--color-success, #22c55e)' },
  not_applicable: { icon: '\u25CB', color: 'var(--main-text-secondary, rgba(255,255,255,0.4))' },
  pending: { icon: '\u25D0', color: 'var(--color-warning, #f59e0b)' },
  missing: { icon: '\u2014', color: 'var(--color-warning, #f59e0b)' },
  broken: { icon: '\u2717', color: 'var(--color-danger, #ef4444)' },
  redacted: { icon: '\u2592', color: 'var(--color-neutral, #6b7280)' },
  unverifiable: { icon: '?', color: 'var(--color-danger, #ef4444)' },
};

// ============================================================================
// Component
// ============================================================================

export function ProvenanceDrawer({
  chain,
  exportPermission = 'redacted',
}: ProvenanceDrawerProps) {
  const [expandedBands, setExpandedBands] = useState(new Set());
  const [copiedActionId, setCopiedActionId] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportMenuRef = useRef(null);

  useEffect(() => {
    if (!exportMenuOpen) return;
    const handleClickAway = (e: MouseEvent) => {
      if (exportMenuRef.current && !exportMenuRef.current.contains(e.target as Node)) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickAway);
    return () => document.removeEventListener('mousedown', handleClickAway);
  }, [exportMenuOpen]);

  const toggleBand = useCallback((band: string) => {
    setExpandedBands((prev: Set<string>) => {
      const next = new Set(prev);
      if (next.has(band)) next.delete(band);
      else next.add(band);
      return next;
    });
  }, []);

  const copyActionId = useCallback(async () => {
    await navigator.clipboard?.writeText(chain.action_id);
    setCopiedActionId(true);
    setTimeout(() => setCopiedActionId(false), 2000);
  }, [chain.action_id]);

  const exportJson = useCallback((mode: 'redacted' | 'full_audit') => {
    const data = mode === 'redacted' ? redactChain(chain) : chain;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `provenance-${chain.action_id.slice(0, 8)}-${mode}.json`;
    a.click();
    URL.revokeObjectURL(url);
    setExportMenuOpen(false);
  }, [chain]);

  const { verdict, verification } = chain;
  const colors = SEVERITY_COLORS[verdict.severity];

  const bandSummaries = chain.bands.map(b => ({
    band: b.band,
    state: b.state,
    label: b.label,
    shortValue: b.display_value,
    severity: b.severity,
  }));

  return React.createElement('div', {
    'data-testid': 'provenance-drawer',
    style: { padding: '0', fontFamily: 'inherit' },
  },
    // Zone 1: Verdict Panel
    renderVerdictPanel(chain, colors, bandSummaries, copiedActionId, copyActionId, exportMenuOpen, setExportMenuOpen, exportJson, exportPermission, exportMenuRef),
    // Zone 2: Bands
    renderBands(chain.bands, expandedBands, toggleBand),
    // Zone 3: Receipt Chain
    renderReceiptChain(chain.receipts, chain.proof),
    // Zone 4: Integrity Checks
    renderIntegrity(verification),
    // Styles
    React.createElement(ProvenanceDrawerStyles, null),
  );
}

// ============================================================================
// Zone 1: Verdict Panel
// ============================================================================

function renderVerdictPanel(
  chain: ActionProvenanceChain,
  colors: { bg: string; border: string; text: string },
  bandSummaries: ProvenanceBandSummary[],
  copiedActionId: boolean,
  copyActionId: () => void,
  exportMenuOpen: boolean,
  setExportMenuOpen: (v: boolean) => void,
  exportJson: (mode: 'redacted' | 'full_audit') => void,
  exportPermission: 'redacted' | 'full_audit',
  exportMenuRef: { current: HTMLDivElement | null },
) {
  return React.createElement('div', {
    'data-testid': 'provenance-drawer-verdict',
    style: {
      padding: '20px',
      background: colors.bg,
      borderBottom: `1px solid ${colors.border}`,
      borderLeft: `3px solid ${colors.border}`,
    },
  },
    // Shield + action ID
    React.createElement('div', {
      style: { display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '12px' },
    },
      React.createElement(ProvenanceShield, {
        bands: bandSummaries,
        status: chain.status,
        size: 'lg',
        testId: 'provenance-drawer-verdict-shield',
      }),
      React.createElement('div', { style: { flex: 1 } },
        React.createElement('div', {
          style: {
            fontSize: '11px',
            color: 'var(--main-text-secondary)',
            fontFamily: "'SF Mono', 'Cascadia Code', monospace",
            marginBottom: '4px',
          },
        }, `Action ${chain.action_id.slice(0, 16)}\u2026`),
        React.createElement('div', {
          'data-testid': 'provenance-drawer-verdict-title',
          style: { fontSize: '16px', fontWeight: 700, color: colors.text, marginBottom: '4px' },
        }, chain.verdict.title),
        React.createElement('div', {
          'data-testid': 'provenance-drawer-verdict-summary',
          style: { fontSize: '13px', color: 'var(--main-text)', lineHeight: 1.5 },
        }, chain.verdict.summary),
      ),
    ),
    // Action buttons
    React.createElement('div', {
      style: { display: 'flex', gap: '8px', flexWrap: 'wrap' },
    },
      React.createElement('button', {
        'data-testid': 'provenance-drawer-copy-action-id',
        onClick: copyActionId,
        style: actionButtonStyle(),
      }, copiedActionId ? '\u2713 Copied' : 'Copy action ID'),
      React.createElement('div', {
        ref: exportMenuRef,
        style: { position: 'relative', display: 'inline-block' },
      },
        React.createElement('button', {
          'data-testid': 'provenance-drawer-export-menu',
          onClick: () => setExportMenuOpen(!exportMenuOpen),
          style: actionButtonStyle(),
        }, 'Export audit JSON \u25BE'),
        exportMenuOpen && React.createElement('div', {
          style: {
            position: 'absolute',
            top: '100%',
            left: 0,
            marginTop: '4px',
            background: 'var(--card-bg, #1a1a2e)',
            border: '1px solid var(--border-color)',
            borderRadius: '8px',
            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
            zIndex: 10,
            minWidth: '180px',
            overflow: 'hidden',
          },
        },
          React.createElement('button', {
            'data-testid': 'provenance-drawer-export-redacted',
            onClick: () => exportJson('redacted'),
            style: dropdownItemStyle(),
          }, 'Export redacted'),
          React.createElement('button', {
            'data-testid': 'provenance-drawer-export-full',
            onClick: exportPermission === 'full_audit' ? () => exportJson('full_audit') : undefined,
            disabled: exportPermission !== 'full_audit',
            style: {
              ...dropdownItemStyle(),
              opacity: exportPermission === 'full_audit' ? 1 : 0.4,
              cursor: exportPermission === 'full_audit' ? 'pointer' : 'not-allowed',
            },
            title: exportPermission !== 'full_audit' ? 'Requires provenance.export.full_audit permission' : undefined,
          }, 'Export full audit'),
        ),
      ),
    ),
  );
}

// ============================================================================
// Zone 2: Bands
// ============================================================================

function renderBands(
  bands: ProvenanceBand[],
  expandedBands: Set<string>,
  toggleBand: (band: string) => void,
) {
  return React.createElement('div', {
    style: { padding: '16px 20px' },
  },
    React.createElement('div', {
      style: { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--main-text-secondary)', marginBottom: '12px' },
    }, 'Custody Bands'),
    ...bands.map((band, i) => {
      const isExpanded = expandedBands.has(band.band);
      const indicator = STATE_INDICATORS[band.state];

      return React.createElement('div', {
        key: band.band,
        'data-testid': `provenance-drawer-band-${band.band}`,
        style: {
          marginBottom: '2px',
          borderRadius: '8px',
          border: '1px solid var(--border-color, rgba(255,255,255,0.06))',
          overflow: 'hidden',
          transition: 'border-color 200ms ease',
        },
      },
        // Band header (clickable)
        React.createElement('button', {
          'data-testid': `provenance-drawer-band-${band.band}-state`,
          onClick: () => toggleBand(band.band),
          onKeyDown: (e: any) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleBand(band.band); } },
          'aria-expanded': isExpanded,
          'aria-label': `${band.label}: ${band.state}`,
          style: {
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            gap: '10px',
            padding: '10px 14px',
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            color: 'var(--main-text)',
            fontSize: '13px',
            textAlign: 'left',
          },
        },
          React.createElement('span', {
            style: { fontSize: '10px', color: 'var(--main-text-secondary)', transition: 'transform 150ms ease', transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)' },
          }, '\u25B6'),
          React.createElement('span', {
            style: { fontSize: '12px', color: 'var(--main-text-secondary)', minWidth: '18px' },
          }, `${i + 1}.`),
          React.createElement('span', { style: { flex: 1, fontWeight: 500 } }, band.label),
          React.createElement('span', {
            style: { fontSize: '13px', fontWeight: 700, color: indicator.color },
          }, indicator.icon),
          React.createElement('span', {
            style: { fontSize: '11px', color: indicator.color },
          }, band.state === 'not_applicable' ? 'n/a' : band.state),
        ),
        // Display value (always visible beneath header)
        band.display_value && React.createElement('div', {
          style: {
            padding: '0 14px 8px 52px',
            fontSize: '12px',
            color: 'var(--main-text-secondary)',
            fontFamily: "'SF Mono', 'Cascadia Code', monospace",
          },
        }, band.display_value),
        // Expanded detail
        isExpanded && React.createElement('div', {
          'data-testid': `provenance-drawer-band-${band.band}-detail`,
          style: {
            padding: '12px 14px 14px 52px',
            borderTop: '1px solid var(--border-color, rgba(255,255,255,0.06))',
            background: 'var(--card-hover-bg, rgba(255,255,255,0.02))',
          },
        },
          // Why this matters
          band.explanation && React.createElement('div', {
            'data-testid': `provenance-drawer-band-${band.band}-microcopy`,
            style: {
              fontSize: '12px',
              color: 'var(--main-text-secondary)',
              fontStyle: 'italic',
              marginBottom: '12px',
              paddingBottom: '10px',
              borderBottom: '1px dashed var(--border-color, rgba(255,255,255,0.06))',
            },
          },
            React.createElement('span', { style: { fontWeight: 600, fontStyle: 'normal', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', display: 'block', marginBottom: '4px' } }, 'Why this matters'),
            band.explanation,
          ),
          // Audit detail
          Object.keys(band.detail).length > 0 && React.createElement('div', null,
            React.createElement('span', { style: { fontWeight: 600, fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--main-text-secondary)', display: 'block', marginBottom: '6px' } }, 'Audit detail'),
            ...Object.entries(band.detail).map(([key, val]) =>
              React.createElement('div', {
                key,
                style: {
                  display: 'flex',
                  gap: '8px',
                  fontSize: '11px',
                  padding: '3px 0',
                  fontFamily: "'SF Mono', 'Cascadia Code', monospace",
                },
              },
                React.createElement('span', { style: { color: 'var(--main-text-secondary)', minWidth: '140px' } }, key + ':'),
                React.createElement('span', { style: { color: 'var(--main-text)', wordBreak: 'break-all' } }, String(val || '\u2014')),
              ),
            ),
          ),
        ),
      );
    }),
  );
}

// ============================================================================
// Zone 3: Receipt Chain
// ============================================================================

function renderReceiptChain(receipts: ProvenanceReceipt[], proof: ActionProvenanceChain['proof']) {
  const custody = receipts.find(r => r.receipt_type === 'custody');
  const execution = receipts.find(r => r.receipt_type === 'execution');

  return React.createElement('div', {
    style: { padding: '16px 20px', borderTop: '1px solid var(--border-color, rgba(255,255,255,0.06))' },
  },
    React.createElement('div', {
      style: { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--main-text-secondary)', marginBottom: '16px' },
    }, 'Receipt Chain'),
    // Receipt nodes
    React.createElement('div', {
      style: { position: 'relative', paddingLeft: '20px' },
    },
      // Vertical connector line
      React.createElement('div', {
        style: {
          position: 'absolute', left: '8px', top: '12px', bottom: '12px',
          width: '2px', background: 'var(--provenance-chain-line, var(--border-color, rgba(255,255,255,0.12)))',
        },
      }),
      // Custody receipt
      custody && renderReceiptNode(custody, 'custody'),
      // Hash link indicator
      custody && execution && React.createElement('div', {
        style: {
          margin: '8px 0 8px 24px',
          fontSize: '10px',
          fontFamily: "'SF Mono', 'Cascadia Code', monospace",
          color: 'var(--provenance-chain-link, var(--main-accent, #818cf8))',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        },
      },
        '\u2514\u2500\u2500 hash: ',
        React.createElement('span', { style: { opacity: 0.8 } }, custody.receipt_hash.slice(0, 12) + '\u2026'),
        ' \u2500\u2500\u2510',
      ),
      // Execution receipt
      execution && renderReceiptNode(execution, 'execution'),
    ),
  );
}

function renderReceiptNode(receipt: ProvenanceReceipt, type: string) {
  return React.createElement('div', {
    'data-testid': `provenance-drawer-receipt-${type}`,
    style: {
      position: 'relative',
      marginBottom: '12px',
      padding: '12px 16px',
      background: 'var(--provenance-receipt-bg, var(--card-hover-bg, rgba(255,255,255,0.03)))',
      borderRadius: '8px',
      border: '1px solid var(--border-color, rgba(255,255,255,0.06))',
    },
  },
    // Dot
    React.createElement('div', {
      style: {
        position: 'absolute', left: '-16px', top: '16px',
        width: '10px', height: '10px', borderRadius: '50%',
        background: receipt.outcome === 'success' || receipt.outcome === 'pending'
          ? 'var(--color-success, #22c55e)'
          : 'var(--color-warning, #f59e0b)',
        border: '2px solid var(--card-bg, #0b1c3d)',
      },
    }),
    // Header
    React.createElement('div', {
      style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
    },
      React.createElement('span', { style: { fontSize: '13px', fontWeight: 600, color: 'var(--main-text)', textTransform: 'capitalize' } },
        `${type} Receipt`,
      ),
      React.createElement('span', { style: { fontSize: '11px', color: 'var(--main-text-secondary)', fontFamily: "'SF Mono', monospace" } },
        receipt.signed_at ? formatTime(receipt.signed_at) : '',
      ),
    ),
    // Fields
    React.createElement('div', { style: { fontSize: '11px', fontFamily: "'SF Mono', 'Cascadia Code', monospace" } },
      receiptField('outcome', receipt.outcome),
      receiptField('payload_hash', receipt.payload_hash.slice(0, 16) + '\u2026'),
      receiptField('prev_hash', receipt.prev_hash === 'genesis' ? 'genesis' : receipt.prev_hash.slice(0, 16) + '\u2026'),
    ),
  );
}

function receiptField(label: string, value: string) {
  return React.createElement('div', {
    key: label,
    style: { display: 'flex', gap: '8px', padding: '2px 0' },
  },
    React.createElement('span', { style: { color: 'var(--main-text-secondary)', minWidth: '100px' } }, label + ':'),
    React.createElement('span', { style: { color: 'var(--main-text)' } }, value),
  );
}

// ============================================================================
// Zone 4: Integrity Checks
// ============================================================================

function renderIntegrity(verification: ChainVerification) {
  const checks: { field: keyof ChainVerification; label: string }[] = [
    { field: 'custody_signature_valid', label: 'Custody receipt signature valid' },
    { field: 'execution_signature_valid', label: 'Execution receipt signature valid' },
    { field: 'chain_intact', label: 'Chain links intact (prev_hash matches)' },
    { field: 'evidence_hash_matches', label: 'Evidence hash matches evidence pack' },
    { field: 'temporal_order_valid', label: 'Temporal ordering valid' },
    { field: 'tenant_ids_consistent', label: 'Tenant IDs consistent' },
    { field: 'action_ids_consistent', label: 'Action IDs consistent' },
  ];

  return React.createElement('div', {
    'data-testid': 'provenance-drawer-integrity',
    style: { padding: '16px 20px', borderTop: '1px solid var(--border-color, rgba(255,255,255,0.06))' },
  },
    React.createElement('div', {
      style: { fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--main-text-secondary)', marginBottom: '12px' },
    }, 'Server-Verified Integrity'),
    ...checks.map(({ field, label }) => {
      const passed = Boolean(verification[field]);
      return React.createElement('div', {
        key: field,
        'data-testid': `provenance-drawer-integrity-check-${field}`,
        style: {
          display: 'flex', alignItems: 'center', gap: '8px',
          padding: '4px 0', fontSize: '12px',
        },
      },
        React.createElement('span', {
          style: { color: passed ? 'var(--color-success, #22c55e)' : 'var(--color-danger, #ef4444)', fontWeight: 700 },
        }, passed ? '\u2713' : '\u2717'),
        React.createElement('span', {
          style: { color: passed ? 'var(--main-text)' : 'var(--color-danger, #ef4444)' },
        }, label),
      );
    }),
    verification.verified_at && React.createElement('div', {
      style: { marginTop: '8px', fontSize: '11px', color: 'var(--main-text-secondary)', fontFamily: "'SF Mono', monospace" },
    }, `Verified at: ${verification.verified_at}`),
  );
}

// ============================================================================
// Helpers
// ============================================================================

function actionButtonStyle(): Record<string, any> {
  return {
    padding: '6px 12px',
    fontSize: '12px',
    fontWeight: 500,
    borderRadius: '6px',
    border: '1px solid var(--border-color, rgba(255,255,255,0.1))',
    background: 'var(--card-bg, rgba(255,255,255,0.04))',
    color: 'var(--main-text)',
    cursor: 'pointer',
    transition: 'all 150ms ease',
  };
}

function dropdownItemStyle(): Record<string, any> {
  return {
    display: 'block',
    width: '100%',
    padding: '10px 14px',
    fontSize: '12px',
    textAlign: 'left',
    background: 'transparent',
    border: 'none',
    color: 'var(--main-text)',
    cursor: 'pointer',
    transition: 'background 100ms ease',
  };
}

function redactChain(chain: ActionProvenanceChain): Partial<ActionProvenanceChain> {
  return {
    action_id: chain.action_id,
    tenant_id: chain.tenant_id,
    status: chain.status,
    verdict: chain.verdict,
    bands: chain.bands.map(b => ({
      ...b,
      detail: {},
      display_value: b.state === 'redacted' ? '[redacted]' : b.display_value,
    })),
    receipts: chain.receipts.map(r => ({
      ...r,
      jws_preview: '[redacted]',
    })),
    proof: {
      custody_receipt_hash: chain.proof.custody_receipt_hash,
      execution_receipt_hash: chain.proof.execution_receipt_hash,
      evidence_pack_hash: '[redacted]',
      proof_bundle_id: chain.proof.proof_bundle_id,
    },
    verification: chain.verification,
  };
}

function formatTime(isoString: string): string {
  try {
    const d = new Date(isoString);
    return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 } as any);
  } catch {
    return isoString;
  }
}

function ProvenanceDrawerStyles() {
  useEffect(() => {
    const id = 'provenance-drawer-keyframes';
    if (document.getElementById(id)) return;
    const style = document.createElement('style');
    style.id = id;
    style.textContent = `
      @keyframes provenance-band-enter {
        from { opacity: 0; transform: translateX(-8px); }
        to   { opacity: 1; transform: translateX(0); }
      }
      @media (prefers-reduced-motion: reduce) {
        [data-testid*="provenance-drawer-band"] {
          animation: none !important;
        }
      }
    `;
    document.head.appendChild(style);
  }, []);
  return null;
}

export default ProvenanceDrawer;
